import type { TokenPair } from '@todo/shared';

/**
 * Where tokens live between page loads.
 *
 * localStorage is the pragmatic choice for an internal portal and is what the
 * project README specifies. Be aware of the trade: any script running on the
 * page can read it, so a cross-site-scripting bug becomes a stolen session.
 * The higher-security alternative is httpOnly cookies plus CSRF protection,
 * which needs API changes too - this module is the single place that would
 * have to change.
 */

const ACCESS_KEY = 'todo.accessToken';
const REFRESH_KEY = 'todo.refreshToken';

export function getAccessToken(): string | null {
  return safeGet(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return safeGet(REFRESH_KEY);
}

export function storeTokens(tokens: TokenPair): void {
  safeSet(ACCESS_KEY, tokens.accessToken);
  safeSet(REFRESH_KEY, tokens.refreshToken);
}

export function clearTokens(): void {
  safeRemove(ACCESS_KEY);
  safeRemove(REFRESH_KEY);
}

/**
 * localStorage throws in a few real situations - Safari private browsing,
 * disabled storage, exceeded quota. A throw here would break the whole app,
 * so every access is wrapped and failure degrades to "not logged in".
 */
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore - the session simply will not survive a reload */
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
